"use client";

import { KeyboardEvent as ReactKeyboardEvent, useEffect, useId, useRef, useState } from "react";

const monthNames = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
] as const;

const weekdayLabels = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"] as const;

function formatDate(date: Date) {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${month}/${day}/${date.getFullYear()}`;
}

function startOfDay(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function isSameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

type TimelineDatePickerProps = {
  name: string;
  required?: boolean;
  /** Overrides the generated id so an external `<label for>` can target the input. */
  id?: string;
  /** Extra classes for the trigger input, e.g. the Webflow `text-field w-input` field styling. */
  inputClassName?: string;
};

export function TimelineDatePicker({ name, required = false, id, inputClassName }: TimelineDatePickerProps) {
  const today = startOfDay(new Date());
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Date | null>(null);
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const rootRef = useRef<HTMLDivElement>(null);
  const inputId = useId();
  const calendarId = useId();

  useEffect(() => {
    if (!open) return;
    const closeOnPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", closeOnPointerDown);
    window.addEventListener("keydown", closeOnEscape, true);
    return () => {
      document.removeEventListener("mousedown", closeOnPointerDown);
      window.removeEventListener("keydown", closeOnEscape, true);
    };
  }, [open]);

  function showMonth(month: number, year: number) {
    setViewMonth(month);
    setViewYear(year);
  }

  function goToPreviousMonth() {
    if (viewMonth === 0) showMonth(11, viewYear - 1);
    else showMonth(viewMonth - 1, viewYear);
  }

  function goToNextMonth() {
    if (viewMonth === 11) showMonth(0, viewYear + 1);
    else showMonth(viewMonth + 1, viewYear);
  }

  function selectDate(date: Date) {
    setSelected(date);
    setOpen(false);
  }

  // The input can't be `readonly`: readonly fields are barred from constraint
  // validation, so `required` would never block a submit. Typing is swallowed
  // here instead, leaving the calendar as the only way to set a value.
  function blockTyping(event: ReactKeyboardEvent<HTMLInputElement>) {
    if (event.key === "Tab" || event.key === "Escape") return;
    event.preventDefault();
    if (event.key === "Enter" || event.key === " ") setOpen(true);
  }

  const firstWeekday = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const days = Array.from({ length: firstWeekday + daysInMonth }, (_, index) => {
    if (index < firstWeekday) return null;
    return index - firstWeekday + 1;
  });

  return (
    <div className="timeline-datepicker" ref={rootRef}>
      <input
        id={id ?? inputId}
        name={name}
        type="text"
        className={`timeline-datepicker__input${inputClassName ? ` ${inputClassName}` : ""}`}
        value={selected ? formatDate(selected) : ""}
        required={required}
        autoComplete="off"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={calendarId}
        onChange={() => {}}
        onKeyDown={blockTyping}
        onClick={() => setOpen(true)}
        onFocus={() => setOpen(true)}
      />
      {open && (
        <div id={calendarId} className="timeline-datepicker__calendar" role="dialog" aria-label="Choose timeline date">
          <div className="timeline-datepicker__header">
            <button type="button" className="timeline-datepicker__nav" aria-label="Previous month" onClick={goToPreviousMonth}>
              ‹
            </button>
            <div className="timeline-datepicker__month">{monthNames[viewMonth]} {viewYear}</div>
            <button type="button" className="timeline-datepicker__nav" aria-label="Next month" onClick={goToNextMonth}>
              ›
            </button>
          </div>
          <div className="timeline-datepicker__weekdays" aria-hidden="true">
            {weekdayLabels.map((label) => (
              <span key={label}>{label}</span>
            ))}
          </div>
          <div className="timeline-datepicker__days">
            {days.map((day, index) => {
              if (day === null) return <span key={`empty-${index}`} className="timeline-datepicker__day timeline-datepicker__day--empty" aria-hidden="true" />;
              const date = new Date(viewYear, viewMonth, day);
              // Today is out too: the form handler validates this field as a
              // strictly future date, and rejects the submission otherwise.
              const disabled = date <= today;
              const isSelected = selected ? isSameDay(date, selected) : false;
              return (
                <button
                  key={`${viewYear}-${viewMonth}-${day}`}
                  type="button"
                  className={`timeline-datepicker__day${disabled ? " timeline-datepicker__day--disabled" : ""}${isSelected ? " timeline-datepicker__day--selected" : ""}`}
                  disabled={disabled}
                  aria-label={formatDate(date)}
                  aria-pressed={isSelected}
                  onClick={() => selectDate(date)}
                >
                  {day}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
